import { downloadBlob } from "./blob-url";

/**
 * How ZPL reaches a physical printer.
 *
 * The API is cloud-hosted and cannot see the warehouse network, so it never talks to a printer.
 * Printing runs through an agent on the operator's own workstation, exactly as the legacy system
 * did. Which agent (Zebra Browser Print, Neodynamic) is not yet decided — both expose a local API
 * and take byte-identical ZPL, so they differ only behind this interface.
 */
export interface LabelDispatcher {
  /** Shown to the operator so they know where a silent print went. */
  readonly name: string;
  /**
   * Whether this dispatcher can print right now.
   *
   * Implementations MUST time-bound this probe (e.g. `fetch(url, { signal: AbortSignal.timeout(1500) })`).
   * `resolveAgentDispatcher` awaits each probe in turn and has no timeout of its own, so an
   * agent that hangs rather than refusing — a stalled local service, or a firewall that DROPs
   * instead of REJECTing — blocks resolution indefinitely and leaves the operator's print
   * button spinning with no fallback to the PDF path. Refusing quickly is always better than
   * answering slowly.
   */
  isAvailable(): Promise<boolean>;
  print(zpl: string, opts?: LabelPrintOptions): Promise<void>;
}

/**
 * Where a dispatcher should put the labels.
 *
 * Two named fields rather than one `target: string`, because the two destinations are not
 * interchangeable: an agent wants a queue name known to the workstation, the download adapter
 * wants a filename. Both are strings, so a single positional parameter lets a printer name be
 * passed where a filename belongs — which does not fail, it silently saves a file called
 * `Zebra B` with no extension. Each adapter reads only the field that applies to it and
 * ignores the rest, so a caller that fills in both is always safe.
 */
export interface LabelPrintOptions {
  /** Agent adapters: the printer/queue name to send to. Ignored by the download adapter. */
  printer?: string;
  /** Download adapter: the name to save under. Ignored by agent adapters. */
  fileName?: string;
}

/**
 * Saves the ZPL as a file. Deliberately NOT registered as an agent: it is a diagnostic for
 * validating output on real hardware, and if it were auto-selected it would always beat the PDF
 * fallback and quietly make the feature worse.
 */
export const downloadZplDispatcher: LabelDispatcher = {
  name: "Download .zpl",
  isAvailable: () => Promise.resolve(true),
  print: (zpl, opts) => {
    downloadBlob(new Blob([zpl], { type: "application/x-zpl" }), opts?.fileName ?? "parcel-labels.zpl");
    return Promise.resolve();
  },
};

const agents: LabelDispatcher[] = [];

/**
 * Registers a workstation-agent adapter. Called once at startup when an adapter ships.
 *
 * Registering the same adapter twice is a no-op: a module imported down two paths, or an
 * effect that re-runs, would otherwise leave a duplicate that gets probed a second time on
 * every resolve — invisible except as a slower print button.
 */
export function registerAgentDispatcher(dispatcher: LabelDispatcher): void {
  if (agents.includes(dispatcher)) return;
  agents.push(dispatcher);
}

/**
 * The first registered agent that answers; null when none is present (PDF path).
 *
 * The download adapter is excluded here by identity rather than by leaving it unregistered:
 * it reports available unconditionally (it genuinely always works, and Task 10 calls its
 * `print` directly), so any code path that registered it — a diagnostic toggle, a test, a
 * future adapter list built from a config — would silently make it beat both a real agent and
 * the PDF fallback. Excluding it at the point of selection makes that impossible to get wrong.
 */
export async function resolveAgentDispatcher(): Promise<LabelDispatcher | null> {
  for (const agent of agents) {
    if (agent === downloadZplDispatcher) continue;
    try {
      if (await agent.isAvailable()) return agent;
    } catch (e) {
      // A missing agent usually surfaces as a refused connection rather than `false`, so a
      // throw is not exceptional and must not propagate — falling back to the PDF path is the
      // correct outcome. But "no agent installed" and "agent installed and broken" both end up
      // as the same silent null, and only the second is actionable, so leave a trace.
      console.debug(`Label agent "${agent.name}" availability probe failed; ignoring it.`, e);
    }
  }
  return null;
}

/** Test-only: clears registrations between cases. */
export function __resetDispatchersForTest(): void {
  agents.length = 0;
}
